import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  signal,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { SessionService } from '@core/services/session.service';
import { ChatService } from '../services/chat.service';
import { ChatMessageComponent } from '../chat-message/chat-message.component';
import { debounceTime, fromEvent, Subject, takeUntil } from 'rxjs';
import { animate, style, transition, trigger } from '@angular/animations';
import { ChatMessage } from '../interfaces/chat.interface';
import { ModalService } from '@features/modals/services/modal.service';
import { fadeInAnimation } from '@core/utils/animations.util';
@Component({
  selector: 'app-chat-messages',
  imports: [ChatMessageComponent],
  templateUrl: './chat-messages.component.html',
  styleUrl: './chat-messages.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    fadeInAnimation,
    trigger('scroll-bottom', [
      transition(':enter', [
        style({
          transform: 'translateY(100%)',
          opacity: 0,
        }),
        animate(
          '0.2s ease-in',
          style({
            transform: 'none',
            opacity: 1,
          })
        ),
      ]),
      transition(':leave', [
        animate(
          '0.2s ease-in',
          style({
            transform: 'translateY(100%)',
            opacity: 0,
          })
        ),
      ]),
    ]),
  ],
})
export class ChatMessagesComponent implements OnDestroy {
  @ViewChild('messages', { static: true }) messagesContainer!: ElementRef;

  showScrollBottomButton = signal(false);
  isActiveScroll = signal(false);
  $destroy = new Subject<void>();

  features = signal([
    {
      title: 'Mute',
      action: (message: ChatMessage) =>
        this.modalService.openModal('AdminUserMuteModal', {
          user: message.profile,
        }),
    },
    {
      title: 'Delete',
      action: (message: ChatMessage) =>
        message && this.chatService.deleteMessage(message),
    },
    {
      title: 'Delete All',
      action: (message: ChatMessage) =>
        this.chatService.deleteMessages(message),
    },
  ]);

  constructor(
    public chatService: ChatService,
    public sessionService: SessionService,
    public viewContainerRef: ViewContainerRef,
    private modalService: ModalService
  ) {
    afterRenderEffect(() => {
      const messages = this.chatService.messages();
      if (messages.length > 0 && !this.isActiveScroll()) {
        this.goDown();
      }
    });
  }

  ngAfterViewInit() {
    fromEvent(this.messagesContainer.nativeElement, 'scroll')
      .pipe(debounceTime(150), takeUntil(this.$destroy))
      .subscribe(() => {
        const element = this.messagesContainer?.nativeElement;
        const scrollOffset =
          element?.scrollHeight - element?.scrollTop - element?.clientHeight;
        const threshold = 10;
        const scrollThreshold = 1;

        this.isActiveScroll.set(
          Math.abs(
            element.scrollTop - (element.scrollHeight - element.clientHeight)
          ) > scrollThreshold
        );
        this.showScrollBottomButton.set((scrollOffset ?? 0) > threshold);
      });
  }

  ngOnDestroy() {
    this.$destroy.next();
    this.$destroy.complete();
  }

  goDown() {
    this.messagesContainer.nativeElement.scrollTo({
      top: this.messagesContainer.nativeElement.scrollHeight,
      behavior: 'smooth',
    });
  }
}
